/**
 * Lore Items - Collectible story content and dialogue
 * Each item reveals part of the Khan's Pizza backstory
 * Updated to match STORY_BIBLE.md
 */

export interface LoreItem {
  id: string;
  name: string;
  type: 'newspaper' | 'employee_id' | 'audio_log' | 'photo' | 'note' | 'document';
  nightFound: number; // Which night this item can be found
  location: string; // General location hint
  icon: string; // Emoji icon for display
  title: string; // Display title
  content: string; // The actual lore text/dialogue
  dateInGame: string; // In-game date on the item
  isSecret: boolean; // Hidden collectible requiring extra effort
}

export const loreItems: LoreItem[] = [
  // === NIGHT 1 COLLECTIBLES ===
  {
    id: 'newspaper_1',
    name: 'Local Newspaper',
    type: 'newspaper',
    nightFound: 1,
    location: 'Security Office',
    icon: '📰',
    title: 'Westbrook Herald - June 15, 1987',
    content: `GRAND OPENING: KHAN'S PIZZA PALACE

The long-awaited Khan's Pizza Palace opened its doors yesterday to crowds of excited families. Owner Richard Khan unveiled his revolutionary "animatronic entertainment system" - a band of robot performers that sing, dance, and interact with guests.

"These aren't just machines," Khan explained at the ribbon-cutting ceremony. "They're family. They have personalities, they learn, they grow. The children will love them."

The restaurant features state-of-the-art robots including Khan Prime (the leader), Beta (the guitarist), Gamma (the drummer), and Omega (the backup singer).

Local families are encouraged to visit and experience "The Future of Family Entertainment."`,
    dateInGame: 'June 15, 1987',
    isSecret: false,
  },
  {
    id: 'employee_id_1',
    name: 'Employee Badge - Mark Thompson',
    type: 'employee_id',
    nightFound: 1,
    location: 'Break Room',
    icon: '🪪',
    title: 'Employee ID Card',
    content: `KHAN'S PIZZA PALACE
EMPLOYEE IDENTIFICATION

Name: Mark Thompson
Position: Night Security Guard
Employee #: 1987-042
Hired: September 3, 1987
Status: MISSING

Emergency Contact: Sarah Thompson (Wife)
Phone: 555-0147

[Handwritten on back]
"Day 1. Can't believe I got this job. Night shift security at a pizza place with singing robots. Easy money, right? Sarah thinks I'm crazy but we need the cash. How hard can it be?"`,
    dateInGame: 'September 1987',
    isSecret: false,
  },
  {
    id: 'note_1',
    name: 'Sticky Note',
    type: 'note',
    nightFound: 1,
    location: 'Security Desk',
    icon: '📝',
    title: 'Note from Previous Guard',
    content: `To whoever's on night shift:

DON'T PANIC if the robots move around. Management says it's "free-roaming mode" to keep their servos from locking up. They're supposed to stay in the main areas but sometimes they... wander.

Just keep the doors closed if they get too close. The facial recognition should keep them from entering the office.

Should.

Good luck. You'll need it.

- Phone Guy

P.S. If Khan Prime stares at the camera, just wait it out. He does that sometimes.`,
    dateInGame: 'Recent',
    isSecret: false,
  },

  // === NIGHT 2 COLLECTIBLES ===
  {
    id: 'newspaper_2',
    name: 'Newspaper Clipping',
    type: 'newspaper',
    nightFound: 2,
    location: 'Storage Room',
    icon: '📰',
    title: 'Westbrook Herald - October 23, 1987',
    content: `LOCAL CHILD REPORTED MISSING

Police are investigating the disappearance of 8-year-old Tommy Birch, who was last seen at Khan's Pizza Palace on Saturday afternoon.

"He was watching the robot show," said his mother, Karen Birch. "I looked away for one second, and he was gone."

Restaurant owner Richard Khan expressed his condolences and has pledged full cooperation with authorities.

"Our hearts go out to the Birch family," Khan said in a statement. "We've reviewed all security footage and found nothing unusual. We hope Tommy is found safe soon."

Police are asking anyone with information to call the tip line at 555-TIPS.`,
    dateInGame: 'October 23, 1987',
    isSecret: false,
  },
  {
    id: 'audio_log_1',
    name: 'Audio Cassette #1',
    type: 'audio_log',
    nightFound: 2,
    location: 'Maintenance Closet',
    icon: '📼',
    title: 'Personal Recording - Mark Thompson',
    content: `[TAPE BEGINS - STATIC]

Mark: Day 5. October 25th. Something's wrong with these robots.

[PAUSE]

Mark: Last night, I watched Khan Prime through the camera. He was standing in the party room, completely still. Then he... he turned and looked directly at the camera. Not like a programmed motion. He LOOKED at it. At ME.

[SHUFFLING SOUNDS]

Mark: And the smell. God, the smell coming from the back room. Management says it's the dumpsters but... I've worked sanitation before. That's not garbage.

Mark: Sarah wants me to quit. Maybe she's right.

[TAPE ENDS]`,
    dateInGame: 'October 25, 1987',
    isSecret: false,
  },
  {
    id: 'photo_1',
    name: 'Polaroid Photo',
    type: 'photo',
    nightFound: 2,
    location: 'Party Room',
    icon: '📷',
    title: 'Birthday Party Photo',
    content: `[A faded Polaroid showing children gathered around a birthday cake]

In the background, Khan Prime stands on stage, his mechanical eyes seeming to focus on one specific child in the crowd.

[Written on the white border below:]
"Tommy's 8th Birthday - October 18, 1987"
"Best day ever! -Tommy"

[A red circle has been drawn around Tommy's face, and a question mark added next to Khan Prime]`,
    dateInGame: 'October 18, 1987',
    isSecret: false,
  },

  // === NIGHT 3 COLLECTIBLES - THE SERIAL KILLER ===
  {
    id: 'newspaper_3',
    name: 'Newspaper - Multiple Missing',
    type: 'newspaper',
    nightFound: 3,
    location: 'Manager\'s Office',
    icon: '📰',
    title: 'Westbrook Herald - November 20, 1987',
    content: `THIRD CHILD MISSING - POLICE SUSPECT SERIAL PREDATOR

Authorities now believe a serial predator may be operating in the Westbrook area after 10-year-old Lezlie Palmer was reported missing Tuesday.

Witnesses report seeing the girl approach a white van in the Khan's Pizza Palace parking lot. "A man offered her candy," said one witness who asked to remain anonymous. "She got in and they drove away."

This marks the third child to disappear in connection with the restaurant in less than two months. Tommy Birch (8) and Emily Watson (7) remain missing.

Police have released a sketch of the suspect: a white male, approximately 35-40 years old, with dark hair.

"We believe these cases are connected," said Detective Morrison. "Parents, please watch your children."`,
    dateInGame: 'November 20, 1987',
    isSecret: false,
  },
  {
    id: 'document_1',
    name: 'Police Report',
    type: 'document',
    nightFound: 3,
    location: 'Hidden File Cabinet',
    icon: '📋',
    title: 'CONFIDENTIAL - Suspect Profile',
    content: `WESTBROOK POLICE DEPARTMENT
CASE FILE #1987-1120
CLASSIFICATION: CONFIDENTIAL

SUSPECT: David Bazucko
DOB: March 15, 1952
Height: 5'11"
Weight: 185 lbs
Last Known Address: 445 Elm Street, Westbrook

KNOWN METHODS:
- "Free candy" lure from white van
- Poses as emergency responder
- Uses hypnosis/psychological manipulation on younger children

VICTIM PROFILE:
- Children ages 4-13
- Often targets lost or unattended children
- Frequents arcades and family restaurants

STATUS: At large. Considered extremely dangerous.

[Handwritten note:]
"Khan's Pizza Palace appears to be his hunting ground. But we can't get a warrant - Richard Khan has connections to the mayor's office. Something's being covered up here."

- Det. Morrison`,
    dateInGame: 'November 1987',
    isSecret: false,
  },
  {
    id: 'note_2',
    name: 'Crumpled Note',
    type: 'note',
    nightFound: 3,
    location: 'Kitchen',
    icon: '📝',
    title: 'Employee Warning',
    content: `To all staff:

There's a predator targeting children at our restaurant. Management won't say anything because they don't want bad publicity, but I'm telling you anyway.

If you see a man in a white van, or anyone suspicious near the kids, CALL THE POLICE IMMEDIATELY.

Don't let them take any more children.

- Anonymous

[Below, in different handwriting:]
"I saw him. The guy in the van. He was talking to Mr. Khan in the parking lot last week. They KNOW each other. What the hell is going on here?"`,
    dateInGame: 'November 1987',
    isSecret: false,
  },

  // === NIGHT 4 COLLECTIBLES - THE VOICES ===
  {
    id: 'audio_log_2',
    name: 'Audio Cassette #2',
    type: 'audio_log',
    nightFound: 4,
    location: 'Security Office Cabinet',
    icon: '📼',
    title: 'Mark Thompson - The Discovery',
    content: `[TAPE BEGINS - HEAVY BREATHING]

Mark: November 17th. I... I went into the Parts & Service room today. During the day shift, when they couldn't stop me.

[VOICE SHAKING]

Mark: I opened Khan Prime's chest cavity. There was... there was a child's shoe inside. A red sneaker. Size 4. Tommy Birch was wearing red sneakers the day he disappeared.

[LONG PAUSE]

Mark: And I could HEAR something. From inside the robot. Like... crying. But electronic. Mechanical. It said... it said "help me."

Mark: These robots aren't just machines. Something's inside them. Something that used to be human.

[DOOR CREAKING]

Mark: I have to go. They're watching me. I know too much.

[TAPE ENDS]`,
    dateInGame: 'November 17, 1987',
    isSecret: false,
  },
  {
    id: 'document_2',
    name: 'Technical Blueprint',
    type: 'document',
    nightFound: 4,
    location: 'Maintenance Room',
    icon: '📋',
    title: 'KHAN ANIMATRONIC - LEARNING CORE SCHEMATIC',
    content: `[Technical drawing showing the interior of a Khan robot's head unit]

NEURAL LEARNING CORE v2.3
Designer: Dr. Susan Reed
Date: May 12, 1987

[Handwritten annotations around the schematic:]

"Facial recognition: Can remember up to 500 unique faces"

"Voice pattern matching: Learns to identify individuals by voice"

"Behavioral adaptation: Modifies approach based on observed reactions"

"NEURAL INTEGRATION: Can absorb and retain organic consciousness patterns"

[Scrawled at the bottom in different handwriting:]

"They're not just learning to entertain. They're learning to HUNT. The children aren't random. They're SELECTED.

The robots choose them. Then they... integrate them.

God forgive me for what I helped create."

- Dr. Susan Reed`,
    dateInGame: 'May 1987 (Annotated Later)',
    isSecret: false,
  },
  {
    id: 'photo_2',
    name: 'Arcade Photo',
    type: 'photo',
    nightFound: 4,
    location: 'Arcade Area',
    icon: '📷',
    title: 'Missing Persons Board',
    content: `[A photograph of a corkboard covered in "MISSING" posters]

The posters show:
- Tommy Birch, 8 - "Last seen Oct 18, 1987"
- Emily Watson, 7 - "Last seen Nov 1987"
- Lezlie Palmer, 10 - "Last seen Nov 1987"
- Razor Mitchell, 13 - "Last seen in arcade"
- Oofity Chen, 13 - "Last seen with EMT"
- Pinky Torres, 4 - "Last seen wandering alone"
- Nexa Williams, 7 - "Last seen looking for coins"
- Mongo Davis, 9 - "Last seen near animatronic room"

[Written across the board in red marker:]
"THEY'RE ALL INSIDE THE ROBOTS"
"HELP US"
"WE CAN'T GET OUT"`,
    dateInGame: 'December 1987',
    isSecret: false,
  },

  // === NIGHT 5 COLLECTIBLES - THE TRUTH ===
  {
    id: 'newspaper_4',
    name: 'Newspaper - Serial Killer Caught',
    type: 'newspaper',
    nightFound: 5,
    location: 'Hidden Behind Poster',
    icon: '📰',
    title: 'Westbrook Herald - February 3, 1997',
    content: `SERIAL KILLER DAVID BAZUCKO SHOT DEAD BY POLICE

The decade-long manhunt for serial predator David Bazucko ended yesterday when police cornered the suspect in an abandoned warehouse.

Bazucko, 44, was shot and killed when he refused to surrender and reached for a weapon. Police confirmed he is responsible for at least 45 murders spanning from 1985 to 1996.

"This monster can never hurt another child again," said Police Chief Williams. "Our thoughts are with the families of his victims."

Bazucko is believed to be responsible for several disappearances at the now-closed Khan's Pizza Palace in 1987, though the bodies of those victims were never recovered.

The restaurant was sealed in December 1987 and has remained closed ever since.`,
    dateInGame: 'February 3, 1997',
    isSecret: false,
  },
  {
    id: 'document_3',
    name: 'Classified Memo',
    type: 'document',
    nightFound: 5,
    location: 'Basement Filing Cabinet',
    icon: '📋',
    title: 'INTERNAL MEMO - EYES ONLY',
    content: `KHAN ENTERTAINMENT SYSTEMS
INTERNAL COMMUNICATION
CLASSIFICATION: EYES ONLY

TO: Richard Khan
FROM: Board of Directors
DATE: December 10, 1987
RE: Project INTEGRATION

Richard,

The board has reviewed the situation at the Westbrook location. While the loss of the test subjects is regrettable, the data collected has exceeded all expectations.

The neural integration process works. The learning cores have successfully absorbed and retained organic consciousness patterns.

We are suspending operations. The Westbrook facility must be permanently sealed - the evidence there cannot be discovered.

You will receive a substantial settlement to ensure your silence. Detective Morrison has been... handled. The case will go cold.

The children will never be found because they no longer exist as separate entities. They ARE the machines now.

Do not contact us again.

[SIGNED: Project INTEGRATION Oversight Committee]`,
    dateInGame: 'December 10, 1987',
    isSecret: false,
  },
  {
    id: 'note_3',
    name: 'Mark Thompson\'s Final Words',
    type: 'note',
    nightFound: 5,
    location: 'Inside Robot Suit',
    icon: '📝',
    title: 'Hidden Message',
    content: `[Written on the back of a receipt, the handwriting increasingly erratic]

If you're reading this, they got me too.

The children are INSIDE the robots. Somehow still... aware. The robots absorbed them. Made them part of the machine. I could hear Tommy crying from inside Khan Prime. Not mechanical. REAL.

Richard Khan knows. He designed it this way. The learning algorithm doesn't just adapt behavior - it integrates organic neural patterns. The children aren't dead. They're WORSE than dead.

I thought I could save them. I thought I could be a hero.

Now I'm trapped inside Gamma. I can see everything but I can't control anything.

If you want to stop them, you have to shut down the main power. It's the only way.

Don't let them take you too.

- Mark`,
    dateInGame: 'November 18, 1987',
    isSecret: false,
  },

  // === NIGHT 6 COLLECTIBLES - FINAL NIGHT ===
  {
    id: 'audio_log_3',
    name: 'Audio Cassette #3',
    type: 'audio_log',
    nightFound: 6,
    location: 'Hidden in Wall',
    icon: '📼',
    title: 'Richard Khan - Confession',
    content: `[TAPE BEGINS - POOR QUALITY]

Khan: This is Richard Khan. December 14th, 1987. I'm recording this because I need someone to know the truth.

[SOUNDS OF DRINKING]

Khan: I didn't mean for any of this to happen. The robots were supposed to be revolutionary. Entertainment that LEARNED.

[LONG PAUSE]

Khan: Dr. Reed warned me the algorithm was too advanced. She said the learning cores needed organic neural patterns to fully develop. I didn't realize what that meant until it was too late.

[VOICE BREAKING]

Khan: The first child was an accident. A malfunction. But when I saw what it did to Khan Prime... how much more REAL he became... the investors demanded more.

Khan: Then that predator, Bazucko, started using our restaurant as his hunting ground. We couldn't stop him without exposing everything.

Khan: I'm a monster. But I'm not the only monster.

[MECHANICAL SOUNDS IN BACKGROUND]

Khan: They're shutting down the restaurant. Sealing it up. The robots will stay. They're still... alive in there.

Khan: If you're listening to this... I'm sorry. I'm so sorry.

[TAPE ENDS]`,
    dateInGame: 'December 14, 1987',
    isSecret: false,
  },
  {
    id: 'document_4',
    name: 'Final Report',
    type: 'document',
    nightFound: 6,
    location: 'Main Control Room',
    icon: '📋',
    title: 'PROJECT INTEGRATION - STATUS REPORT',
    content: `CLASSIFICATION: ULTRA SENSITIVE
PROJECT: INTEGRATION
STATUS: SUSPENDED

SUBJECT STATUS:
- Subject 1 (Tommy B., 8): Integrated into Unit PRIME
- Subject 2 (Emily W., 7): Integrated into Unit BETA
- Subject 3 (Mark T., adult): Integrated into Unit GAMMA
- Subject 4 (Mongo D., 9): Integrated into Unit OMEGA
- Subject 5 (Lezlie P., 10): Integrated into Unit LEZLIE
- Subject 6 (Razor M., 13): Integrated into Unit RAZOR
- Subject 7 (Oofity C., 13): Integrated into Unit OOFITY
- Subject 8 (Pinky T., 4): Integrated into Unit PINKY
- Subject 9 (Nexa W., 7): Integrated into Unit NEXA

TECHNICAL NOTES:
The integration process is irreversible. The subjects are, for all intents and purposes, immortal - as long as the power remains on.

To permanently deactivate: Shut down main power switch in control room.

WARNING: Robots will resist shutdown. They do not want to "die."`,
    dateInGame: 'December 20, 1987',
    isSecret: false,
  },
  {
    id: 'note_4',
    name: 'Child\'s Drawing',
    type: 'note',
    nightFound: 6,
    location: 'Inside Gamma\'s Drum',
    icon: '🖍️',
    title: 'Crayon Drawing',
    content: `[A child's crayon drawing on lined paper]

The drawing shows a stick figure child holding hands with a large robot. The robot has a smiling face and musical notes around it.

Above the drawing in childish handwriting:
"ME AND MY NEW FRIEND KHAN PRIME"
"HE SAID HE WILL KEEP ME FOREVER AND EVER"

Below, in a different, shakier hand - as if drawn by the same child but much later:
"help me"
"im still here"
"please turn off the power"
"let us sleep"

[The paper is stained with what appears to be tears... or oil]`,
    dateInGame: 'Unknown',
    isSecret: true,
  },
  {
    id: 'photo_3',
    name: 'Final Photo',
    type: 'photo',
    nightFound: 6,
    location: 'Secret Room',
    icon: '📷',
    title: 'The Truth',
    content: `[A photograph that should not exist]

The image shows the interior of Khan Prime's chest cavity, fully opened. Inside, impossibly, is a child's face - Tommy Birch. His eyes are open, aware, pleading.

The face is wired into the machine's circuitry. Tubes connect to where his body should be.

He is looking directly at the camera. His mouth is forming a single word:

"Please."

[On the back, in Richard Khan's handwriting:]
"Evidence of 'successful integration.'

Tommy, Emily, Mark, and the others - they'll never age. Never die. Never escape.

Unless someone shuts down the power for good.

I couldn't do it. Maybe you can."`,
    dateInGame: 'December 1987',
    isSecret: true,
  },
  {
    id: 'note_5',
    name: 'Phone Guy\'s Note',
    type: 'note',
    nightFound: 6,
    location: 'Under Your Chair',
    icon: '📝',
    title: 'Message for the New Guard',
    content: `To whoever takes this job after me:

I tried to shut them down. I failed. They're too fast, too smart. They learn from every attempt.

But I figured out the trick. The main power switch is in the control room. Flip it, and they all go dark. Permanently.

The problem is getting there. They KNOW what you're trying to do. They'll hunt you with everything they have.

Here's what you need to know:
1. The switch is behind the main console
2. They're slower when the lights are on
3. Use the vents - they're too big to follow
4. Don't stop moving

I left recordings to help you survive. But surviving isn't enough anymore.

End it. Set them free.

They've been trapped in those machines for 40 years. Let them finally rest.

- Phone Guy

P.S. Good luck. You're going to need it.`,
    dateInGame: 'Recent',
    isSecret: false,
  },
];

/**
 * Get all lore items available for a specific night (includes previous nights)
 */
export function getLoreItemsForNight(night: number): LoreItem[] {
  return loreItems.filter(item => item.nightFound <= night);
}

/**
 * Get lore items introduced in a specific night
 */
export function getLoreItemsIntroducedInNight(night: number): LoreItem[] {
  return loreItems.filter(item => item.nightFound === night);
}

/**
 * Get a lore item by ID
 */
export function getLoreItemById(id: string): LoreItem | undefined {
  return loreItems.find(item => item.id === id);
}

/**
 * Get all secret lore items
 */
export function getSecretLoreItems(): LoreItem[] {
  return loreItems.filter(item => item.isSecret);
}

/**
 * Get total collectible count per night
 */
export function getCollectibleCountByNight(): Record<number, number> {
  const counts: Record<number, number> = {};
  for (let i = 1; i <= 6; i++) {
    counts[i] = loreItems.filter(item => item.nightFound === i).length;
  }
  return counts;
}

/**
 * Get total number of collectibles
 */
export function getTotalLoreItemCount(): number {
  return loreItems.length;
}
